import React, { useEffect, useState, useMemo, useCallback } from "react";
import "../Styles/HomePage.css";
import { useMediaQuery } from "react-responsive";
import { DataComp } from "../Component/DataComp";
import "react-loading-skeleton/dist/skeleton.css";
import { API_ROOT, webPath } from "../apiconfig";
import "../Styles/Event.css";
import { useNavigate } from "react-router-dom";

const Eventpage = () => {
  const [allEvents, setAllEvents] = useState([]);
  const [visibleData, setVisibleData] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [currentTab, setCurrentTab] = useState("all");

  const ITEMS_PER_PAGE = 9;
  const navigate = useNavigate();

  const isLargeScreen = useMediaQuery({ query: "(min-width: 768px)" });
  const isSmallScreen = useMediaQuery({ query: "(max-width: 320px)" });

  const dataCompWidth = useMemo(() => {
    if (isLargeScreen) return 500;
    if (isSmallScreen) return 250;
    return 300;
  }, [isLargeScreen, isSmallScreen]);

  const generateSrcSet = (imagePath, widths) =>
    widths.map((width) => `${imagePath}?width=${width} ${width}w`).join(", ");

  const cleanAndTruncate = (text, maxLength) => {
    const cleaned = text.replace(/<\/?[^>]+(>|$)/g, "").trim();
    return cleaned.length > maxLength ? `${cleaned.slice(0, maxLength)}...` : cleaned;
  };

  const fetchData = useCallback(async (tab, pageNumber) => {
    try {
      setLoading(true);
      let endpoint = "";
      if (tab === "upcoming") endpoint = "/api/event/upcoming_events";
      else if (tab === "past") endpoint = "/api/event/past_events";
      else endpoint = "/api/event/totalevents";

      const response = await fetch(`${API_ROOT}${endpoint}?page=${pageNumber}`);
      const result = await response.json();

      const newItems = result?.responseData?.eventData || [];

      if (newItems.length > 0) {
        setAllEvents((prev) => {
          const existingIds = new Set(prev.map((item) => item.event_id));
          const uniqueItems = newItems.filter((item) => !existingIds.has(item.event_id));
          const combined = [...prev, ...uniqueItems];
          // Update visible data based on new total
          setVisibleData(combined.slice(0, visibleData.length + ITEMS_PER_PAGE));
          return combined;
        });

        const totalPages = result?.responseData?.pagination?.totalPages || 1;
        if (pageNumber >= totalPages) {
          setHasMore(false);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [visibleData.length]);

  // Initial fetch/reset on tab change
  useEffect(() => {
    const loadInitialData = async () => {
      setAllEvents([]);
      setVisibleData([]);
      setPage(1);
      setHasMore(true);
      try {
        setLoading(true);
        let endpoint = "";
        if (currentTab === "upcoming") endpoint = "/api/event/upcoming_events";
        else if (currentTab === "past") endpoint = "/api/event/past_events";
        else endpoint = "/api/event/totalevents";

        const response = await fetch(`${API_ROOT}${endpoint}?page=1`);
        const result = await response.json();
        const newItems = result?.responseData?.eventData || [];

        setAllEvents(newItems);
        setVisibleData(newItems.slice(0, ITEMS_PER_PAGE));
        const totalPages = result?.responseData?.pagination?.totalPages || 1;
        if (1 >= totalPages || newItems.length < ITEMS_PER_PAGE) {
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching initial events:", error);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [currentTab]);

  const handleTabClick = (tab) => setCurrentTab(tab);

  const handleLoadMore = () => {
    const nextVisibleCount = visibleData.length + ITEMS_PER_PAGE;

    if (nextVisibleCount <= allEvents.length) {
      setVisibleData(allEvents.slice(0, nextVisibleCount));
    } else if (hasMore) {
      setPage((prevPage) => {
        const nextPage = prevPage + 1;
        fetchData(currentTab, nextPage);
        return nextPage;
      });
    } else {
      setVisibleData(allEvents);
    }
  };

  return (
    <div className="container container-max mt-3 mb-2 main-page">
      <div className="row">
        <div className="col-12 borderB d-flex" style={{ padding: "0px" }}>
          {["all", "upcoming", "past"].map((tab) => (
            <button
              key={tab}
              style={{
                border: "none",
                backgroundColor: "#dee2e6",
                borderTopRightRadius: "10px",
                borderTopLeftRadius: "10px",
                padding: "12px"
              }}
              className={`rounded-top custom-hover btn btn-event ms-1 ${
                currentTab === tab ? "active-tab" : "text-black"
              }`}
              onClick={() => handleTabClick(tab)}
            >
              <h4 className="fw-bold h6" style={{ fontSize: "14px" }}>
                {tab === "all" ? "All Events" : tab === "upcoming" ? "Upcoming Events" : "Past Events"}
              </h4>
            </button>
          ))}
        </div>

        <div className="col-12 container" style={{ overflow: "hidden", minHeight: "700px" }}>
          <div className="mainSecondBox mt-1">
            {visibleData.length > 0 ? (
              <div className="row g-3">
                {visibleData.map((item) => (
                  <div key={item.event_id} className="col-lg-4 col-md-6 col-12">
                    <a
                      href={`/events/${item.event_slug}`}
                      style={{ textDecoration: "none", color: "inherit" }}
                    >
                      <div className="event-card">
                        <DataComp
                          src={`${webPath}${item.event_banner}?width=${dataCompWidth}`}
                          srcSet={generateSrcSet(`${webPath}${item.event_banner}`, [
                            150, 250, 300, 320, 500, 700, 1000, 1200
                          ])}
                          alt={item.event_title}
                          h2Title={<h2 className="event-title">{item.event_title}</h2>}
                          post_location={
                            <div className="event-location">
                              <span className="fw-bold">{item.event_location}</span>
                              <span>
                                {" "}
                                |{" "}
                                {new Intl.DateTimeFormat("en-US", {
                                  month: "short",
                                  day: "2-digit",
                                  year: "numeric"
                                }).format(new Date(item.event_date))}
                              </span>
                            </div>
                          }
                          p_Desc={
                            <p className="event-description">
                              {cleanAndTruncate(item.event_desc, 100)}
                            </p>
                          }
                          withZoom={true}
                          fetchpriority="low"
                        />
                      </div>
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center mt-5"><h4></h4></div>
            )}
          </div>

          {(visibleData.length < allEvents.length || hasMore) && (
            <div className="d-flex justify-content-center mt-3">
              <button
                onClick={handleLoadMore}
                className="btn btn-dark w-20"
                disabled={loading}
              >
                {loading ? "Loading..." : "Show More"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Eventpage;
